import * as React from "react";
import PropTypes from "prop-types";

import "./styles.css";
import GuestsSelector from "../guests-selector";
import SelectDateRange from "../date-and-time/select-date-range";
import ExperiencePrice from "../price/experience-price";
import { computeTimeRange, cmpTime } from "../date-and-time/utils";
import {
  connectToProps,
  Moment,
  _,
  experiencesTimesPropType,
  numSelectedExperiencesGuestsPropType,
  experiencePropType,
  experiencesDatesPropType
} from "../../model/utils";

const ExperienceDataForm = ({
  experience: {
    id: experienceId,
    title,
    morningSlotStart,
    morningSlotEnd,
    afternoonSlotStart,
    afternoonSlotEnd
  },
  experiencesDates,
  setExperiencesDates,
  experiencesTimes,
  setExperiencesTimes,
  numSelectedExperiencesGuests,
  setNumSelectedExperiencesGuests,
  large,
  dateRanges,
  experiencesDatesError
}) => {
  const allowedDateRanges = dateRanges
    ? dateRanges.map(({ dateFrom, dateTo }) => [dateFrom, dateTo])
    : [];

  const allowedTimes = getTimeSlotsFromRange(
    morningSlotStart,
    morningSlotEnd
  ).concat(getTimeSlotsFromRange(afternoonSlotStart, afternoonSlotEnd));

  const [numGuests, setNumGuests] = useMap(
    experienceId,
    numSelectedExperiencesGuests,
    setNumSelectedExperiencesGuests
  );

  const [experienceDate, setExperienceDate] = useMap(
    experienceId,
    experiencesDates,
    setExperiencesDates
  );

  const [experienceTime, setExperienceTime] = useMap(
    experienceId,
    experiencesTimes,
    setExperiencesTimes
  );

  const guests = (
    <>
      <div styleName="select-label">{_.numPartecipants}</div>
      <GuestsSelector
        fieldName="experience_guests"
        numGuests={numGuests}
        setNumGuests={setNumGuests}
        maxGuests={99}
      />
    </>
  );

  const experienceDateRange = (
    <>
      <SelectDateRange
        fieldName="experience_day"
        label={_.experienceDate}
        isDateRange={false}
        date={experienceDate}
        setDate={setExperienceDate}
        time={experienceTime}
        setTime={setExperienceTime}
        timeData={{
          allowedTimes,
          minMorningTime: morningSlotStart,
          maxMorningTime: morningSlotEnd,
          minAfternoonTime: afternoonSlotStart,
          maxAfternoonTime: afternoonSlotEnd,
          timeStep: 15
        }}
        allowedDateRanges={allowedDateRanges}
        error={experiencesDatesError || ""}
      />
    </>
  );

  const expPrice = <ExperiencePrice experienceId={experienceId} />;

  return (
    <div styleName="container">
      {!large && (
        <div className="row">
          <div className="col-12">
            <div styleName="experience-title">{title}</div>
          </div>
        </div>
      )}
      <div className="row">
        {large ? (
          <>
            <div className="col-sm-6 col-lg-4">{guests}</div>
            <div className="col-sm-6 col-lg-4 offset-lg-1">
              {experienceDateRange}
            </div>
            <div className="col-sm-12 col-lg-3">{expPrice}</div>
          </>
        ) : (
          <>
            <div className="col-12 col-md-6">
              <div className="row">
                <div className="col-12 col-md-4">{guests}</div>
                <div className="col-12 col-md-8">{experienceDateRange}</div>
              </div>
            </div>
            <div className="col-12 col-md-6">{expPrice}</div>
          </>
        )}
      </div>
    </div>
  );
};

const useMap = (experienceId, entriesMap, setEntriesMap) => {
  const entry = (entriesMap || {})[experienceId];
  const setEntry = React.useCallback(
    newEntryValue =>
      setEntriesMap({
        ...(entriesMap || {}),
        [experienceId]: newEntryValue
      }),
    [entriesMap, experienceId, setEntriesMap]
  );
  return [entry, setEntry];
};

ExperienceDataForm.propTypes = {
  experience: experiencePropType,
  experiencesDates: experiencesDatesPropType,
  setExperiencesDates: PropTypes.func.isRequired,
  experiencesTimes: experiencesTimesPropType,
  setExperiencesTimes: PropTypes.func.isRequired,
  numSelectedExperiencesGuests: numSelectedExperiencesGuestsPropType,
  setNumSelectedExperiencesGuests: PropTypes.func.isRequired,
  large: PropTypes.bool,
  dateRanges: PropTypes.arrayOf(
    PropTypes.shape({
      dateFrom: PropTypes.instanceOf(Moment),
      dateTo: PropTypes.instanceOf(Moment)
    })
  ),
  experiencesDatesError: PropTypes.string
};

const getTimeSlotsFromRange = (start, end) =>
  cmpTime(start, end) >= 0 ? [] : computeTimeRange(start, end, 15);

export default connectToProps([
  "experiencesDates",
  "experiencesTimes",
  "numSelectedExperiencesGuests",
  "dateRanges",
  "experiencesDatesError"
])(ExperienceDataForm);
